/**
 * Robust upload service with multiple fallback strategies.
 * 
 * Strategy 1: Direct FormData upload via fetch (no Authorization header = no CORS preflight)
 * Strategy 2: If Network Error, check upload status (server may have processed it despite browser CORS block)
 * Strategy 3: Chunked Base64 upload via axios (legacy fallback)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

function generateUploadId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

/**
 * Compress an image file to reduce size.
 * @param {File} file - Image file
 * @param {number} maxWidth - Max width in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>}
 */
export function compressImage(file, maxWidth = 1000, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Check upload status via a simple GET (no auth headers = no preflight).
 */
async function checkUploadStatus(uploadId, retries = 5) {
  for (let i = 0; i < retries; i++) {
    await new Promise(r => setTimeout(r, 1000 + i * 500));
    try {
      const res = await fetch(`${API_URL}/api/upload/status/${uploadId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'completed') {
          return data;
        }
      }
    } catch {
      // continue retrying
    }
  }
  return null;
}

/**
 * Strategy 1: Direct upload via fetch + FormData.
 * Token is passed as a query parameter to avoid the Authorization header,
 * which would trigger a CORS preflight request that production proxies may block.
 */
async function directUpload(file, token, uploadId) {
  const formData = new FormData();
  formData.append('file', file);

  const url = `${API_URL}/api/upload/direct?token=${encodeURIComponent(token)}&upload_id=${encodeURIComponent(uploadId)}`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    // Deliberately NO headers - browser sets Content-Type with boundary automatically
    // NO Authorization header = NO CORS preflight for simple requests
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Strategy 3: Chunked Base64 upload (legacy fallback).
 */
async function chunkedUpload(file, token, uploadId) {
  const CHUNK_SIZE = 32 * 1024;
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const totalChunks = Math.ceil(bytes.length / CHUNK_SIZE);

  let lastResult = null;
  for (let i = 0; i < totalChunks; i++) {
    const chunk = bytes.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    let binary = '';
    for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
    const b64 = btoa(binary);

    const res = await fetch(`${API_URL}/api/upload/chunk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        filename: file.name,
        chunk_index: i,
        total_chunks: totalChunks,
        upload_id: uploadId,
        data: b64
      })
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Chunk ${i} failed: HTTP ${res.status}`);
    }
    if (i === totalChunks - 1) {
      lastResult = await res.json();
    }
  }
  return lastResult;
}

/**
 * Main upload function with automatic fallback.
 * 
 * @param {File} file - File to upload
 * @param {Object} options - Options
 * @param {boolean} options.isImage - If true, compress the image first
 * @param {number} options.maxWidth - Max image width (default 1000)
 * @param {number} options.quality - JPEG quality (default 0.7)
 * @returns {Promise<{filename: string, url: string, size: number}>}
 */
export async function uploadFile(file, options = {}) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Non authentifie');

  const uploadId = generateUploadId();

  // Compress image if needed
  let fileToUpload = file;
  if (options.isImage && file.type.startsWith('image/')) {
    try {
      const compressed = await compressImage(file, options.maxWidth || 1000, options.quality || 0.7);
      fileToUpload = new File([compressed], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
    } catch {
      // compression failed, use original
    }
  }

  // === Strategy 1: Direct FormData upload (no CORS preflight) ===
  try {
    const result = await directUpload(fileToUpload, token, uploadId);
    if (result && result.url) return result;
  } catch (err) {
    console.warn('[Upload] Direct upload failed:', err.message);
    // If it's a Network Error, the server might have still processed it
    if (err.message === 'Failed to fetch' || err.message?.includes('Network')) {
      const status = await checkUploadStatus(uploadId);
      if (status && status.url) {
        console.info('[Upload] Upload confirmed via status check despite Network Error');
        return status;
      }
    }
  }

  // === Strategy 2: Chunked upload ===
  const chunkUploadId = generateUploadId();
  try {
    const result = await chunkedUpload(fileToUpload, token, chunkUploadId);
    if (result && result.url) return result;
  } catch (err) {
    console.warn('[Upload] Chunked upload failed:', err.message);
    // Check status as fallback
    if (err.message === 'Failed to fetch' || err.message?.includes('Network')) {
      const status = await checkUploadStatus(chunkUploadId);
      if (status && status.url) {
        console.info('[Upload] Chunked upload confirmed via status check');
        return status;
      }
    }
    throw new Error(`Echec upload: ${err.message}`);
  }

  throw new Error('Echec upload: aucune strategie n\'a fonctionne');
}
